/**
 * The product carousels, and the home page's collection list, as the print
 * rows' neighbours on the table, in both eras (snippets/era-head.liquid
 * loads this; assets/custom.css lays the rows out, and in the 1960s
 * assets/analog.css lays the cards out as prints, each at its lean).
 *
 * The carousels are the theme's: its scroller, its snapping, its arrows, its
 * prev and next, and the product tiles and collection cards the theme's own
 * markup puts in them, which the 1960s' stylesheet makes prints with their
 * names printed under them. They stay where the theme put them, and this
 * script, as assets/reviews-row.js does for the review cards, adds what only
 * the print row's engine (assets/print-row.js) can say: each card's seeded
 * place and lean in the row, from the engine's own (PrintRow.tileStyle), as
 * custom properties on the card (--rot, --drop, --shift, --z: the 1960s rest
 * each card at a part of them, the 2020s straight and level), keyed by the
 * product's or collection's own address so a card leans the same way whatever
 * its place in the list or the page it is on (whether it sits high or low
 * alternates along the row, as a row's prints do), and, under a mouse, the
 * engine's own cursor field (PrintRow.magnet): the card the cursor is on rises,
 * grows and leans toward it, tipping toward the viewer, and the cards beside it
 * make room, as a row's prints do, at a fraction of the strength (a card is a
 * print with its name under it, and a shopper is choosing, not browsing a
 * table). The field is told how the stylesheet rests each card, so it starts
 * from that pose and hands the card back to it, and told again whenever the
 * cards change. It stands in for the print's hover lift in the 1960s, and the
 * theme's zoom of a collection card's photo, which the stylesheets give up in a
 * row the field is moving. It has nothing to do with a click, which reaches the
 * card's link as it always has, and a touch screen or a reader who asks for
 * reduced motion gets none of it (the cards keep their seeded places, which are
 * still, as a row's prints do not move on their own here; where the stylesheet
 * rests the cards straight, as the 2020s' does, there is nothing to place, so
 * such a row is left as the theme's and the engine is not fetched for it).
 *
 * Nothing here moves a row by itself: the shopper is choosing the products.
 *
 * Which carousels is said once, below. A host is marked (data-print-row) as
 * it is taken, which is what the stylesheet lays its room and tokens on, and
 * (data-print-field) once the field has it. Some are rendered with the page
 * and some arrive after it (the product page's recommendations, the recently
 * viewed products), so the page is watched for new ones. Each is taken once
 * it nears view, and the engine, which is not loaded until something wants it
 * (a page without a carousel never fetches it), is fetched then, from the
 * address the tag that loads this script carries (data-engine-src).
 *
 * Fails quietly: nothing here throws into the page, and a carousel the script
 * never reaches is the theme's, with prints on it as the stylesheet draws
 * them.
 */
(() => {
  // Where the engine is, for a page that has not loaded it yet.
  const ENGINE_SRC = document.currentScript?.dataset.engineSrc;
  // How far from view a carousel is when it is taken: far enough that its
  // cards are placed before a shopper sees them.
  const NEAR = "150% 0px";
  // The sections whose product carousels these are: the home page's heroes
  // and the product page's recommendations, not the cart drawer's, the
  // landing page's or the search results'.
  const PRODUCT_SECTIONS = [
    ".shopify-section--hero-collection",
    ".shopify-section--product-recommendations",
    ".shopify-section--recently-viewed-products",
    ".shopify-section--featured-collections"
  ].join(", ");

  // What the field does to a card, against what it does to a print (the names
  // are the engine's, in px and degrees; assets/print-row.js): rise, grow,
  // lean toward the pointer, tip in 3D, and what the cards beside it do (sway,
  // tip away, how far each push reaches and how quickly it weakens, so that
  // cards a gap apart are not pushed onto each other, and how much of a
  // print's clearance they are pushed, a card rising and growing less than a
  // print and so needing less room).
  const TUNE = {
    lift: 10,
    grow: 0.025,
    pull: 0.03,
    tilt: 4,
    sway: 8,
    lean: 0.9,
    reach: 4,
    falloff: 0.3,
    clear: 0.3
  };

  // The two kinds of carousel: where each is, the parts of it the engine
  // needs, and the cards in it. A product carousel scrolls its own tiles (the
  // scroller and the list are one element, the cards its children), a
  // collection list scrolls a list inside it.
  const KINDS = [
    {
      hosts: `:is(${PRODUCT_SECTIONS}) product-list`,
      parts: (host) => {
        const list = host.querySelector(
          ".product-list__inner--scroller:not(.product-list__inner--desktop-no-scroller)"
        );
        return list && { scroller: list, list, scrolls: true };
      },
      cards: ":scope > product-item",
      tune: TUNE
    },
    {
      hosts: ".shopify-section--collection-list .list-collections--carousel",
      parts: (host) => {
        const scroller = host.querySelector(".list-collections__scroller");
        const list = scroller?.querySelector(".list-collections__item-list");
        return list && { scroller, list, scrolls: false };
      },
      cards: ":scope > .list-collections__item",
      tune: TUNE
    }
  ];

  const HOSTS = KINDS.map((kind) => kind.hosts).join(", ");

  // Whether the cursor field can run here: a mouse, and motion allowed, as
  // the engine itself decides (assets/print-row.js).
  const FIELD =
    window.matchMedia("(hover: hover) and (pointer: fine)").matches &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const TOKENS = ["--row-lean", "--row-drop", "--row-shift"];

  // Whether a carousel has nothing for the engine to do: no field to move
  // its cards, and a stylesheet that rests them straight and level, taking
  // none of the place and lean the engine would deal them (the 2020s'). It
  // is left as the theme's, and the engine is not fetched for it.
  const idle = (host, kind) => {
    if (FIELD) return false;
    const parts = kind.parts(host);
    if (!parts) return true;
    host.setAttribute("data-print-row", "");
    const tokens = getComputedStyle(parts.list);
    const level = TOKENS.every(
      (name) => !parseFloat(tokens.getPropertyValue(name))
    );
    if (level) host.removeAttribute("data-print-row");
    return level;
  };

  // What a card is known by: the address of the product or collection it
  // shows (not its variant or the query a link carries), which is the same
  // wherever the card is.
  const keyOf = (card) => {
    const link = card.closest("a[href]") || card.querySelector("a[href]");
    if (!link) return card.textContent.trim().slice(0, 80);
    return new URL(link.getAttribute("href"), location.href).pathname;
  };

  // A card's place and lean, by its place in the list, as a row would deal it.
  const place = (card, index) => {
    const style = window.PrintRow.tileStyle(`${keyOf(card)}#0`, index);
    Object.entries(style).forEach(([name, value]) => {
      if (card.style.getPropertyValue(name) === value) return;
      card.style.setProperty(name, value);
    });
  };

  // The cards directly in a list, placed, and the field told to read them
  // again at the places they now have.
  const deal = (list, kind) => {
    list.querySelectorAll(kind.cards).forEach(place);
    list.dispatchEvent(new CustomEvent("print-row:tiles"));
  };

  // How the stylesheet rests a card, in pixels and degrees: its seeded place
  // and lean, less what the row's tokens take off them.
  const poseOf = (card) => {
    const tokens = getComputedStyle(card.parentElement);
    const own = (name) => parseFloat(card.style.getPropertyValue(name)) || 0;
    const token = (name) => parseFloat(tokens.getPropertyValue(name)) || 0;
    return {
      shift: own("--shift") * token("--row-shift"),
      drop: own("--drop") * token("--row-drop"),
      rot: own("--rot") * token("--row-lean")
    };
  };

  // The cursor field, attached to a list once. Nothing, without a mouse.
  const attached = new WeakSet();
  const attach = (host, parts, kind) => {
    if (attached.has(parts.list)) return;
    attached.add(parts.list);
    const field = window.PrintRow.magnet?.(parts.scroller, parts.list, {
      slots: kind.cards,
      pose: poseOf,
      tune: kind.tune,
      scrolls: parts.scrolls
    });
    if (field) host.setAttribute("data-print-field", "");
  };

  // A list's cards change (a section reloads in the theme editor, a script
  // adds a few): the new ones get their places, and the field reads them.
  const followed = new WeakSet();
  const follow = (list, kind) => {
    if (followed.has(list)) return;
    followed.add(list);
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        try {
          deal(list, kind);
        } catch (error) {
          console.error("[product-rows] could not place the cards:", error);
        }
      });
    }).observe(list, { childList: true });
  };

  // Takes a carousel: marks it, places its cards, gives them the field.
  const take = (host, kind) => {
    const parts = kind.parts(host);
    if (!parts) return;
    host.setAttribute("data-print-row", "");
    deal(parts.list, kind);
    attach(host, parts, kind);
    follow(parts.list, kind);
  };

  // The engine, once: loaded here if nothing else has loaded it (the footer's
  // Instagram row and the reviews load it too, each its own way), and waited
  // for if something else is loading it.
  let engine = null;
  const loaded = () => {
    engine ||= new Promise((resolve, reject) => {
      if (window.PrintRow) return resolve();
      document.addEventListener("print-row:ready", resolve, { once: true });
      if (!ENGINE_SRC) return;
      const script = document.createElement("script");
      script.src = ENGINE_SRC;
      script.onerror = () => reject(new Error(`${ENGINE_SRC} did not load`));
      document.head.append(script);
    });
    return engine;
  };

  const near = new IntersectionObserver(
    (entries) => {
      entries.forEach(async (entry) => {
        if (!entry.isIntersecting) return;
        near.unobserve(entry.target);
        try {
          const kind = KINDS.find((each) => entry.target.matches(each.hosts));
          if (!kind || idle(entry.target, kind)) return;
          await loaded();
          if (!window.PrintRow?.tileStyle) return;
          take(entry.target, kind);
        } catch (error) {
          console.error("[product-rows] could not lay out the cards:", error);
        }
      });
    },
    { rootMargin: NEAR }
  );

  // The carousels in what the page has just been given (or all of it), each
  // watched once for its coming near.
  const seen = new WeakSet();
  const scan = (root) => {
    const found = root.matches?.(HOSTS)
      ? [root]
      : Array.from(root.querySelectorAll?.(HOSTS) || []);
    found.forEach((host) => {
      if (seen.has(host)) return;
      seen.add(host);
      near.observe(host);
    });
  };

  new MutationObserver((records) => {
    records.forEach((record) =>
      record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) scan(node);
      })
    );
  }).observe(document.documentElement, { childList: true, subtree: true });
  scan(document);
})();
